// src/helpers/fingerprint.js

/**
 * Captures browser fingerprinting details including device info and optionally geolocation.
 * @returns {Promise<Object>} Fingerprint data object.
 */
export const getFingerprint = async () => {
  const fingerprint = {
    device: {
      browser: getBrowserName(),
      os: getOSName(),
      deviceType: getDeviceType(),
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language,
    },
    location: null,
  };

  try {
    // We attempt to get the position, but we don't block heavily if it fails or is denied.
    const position = await getCurrentPosition();
    if (position) {
      fingerprint.location = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy,
      };
    }
  } catch (error) {
    // Geolocation might be denied or unavailable, which is expected.
    console.debug("Geolocation for fingerprinting not available:", error.message);
  }

  return fingerprint;
};

const getBrowserName = () => {
  const ua = navigator.userAgent;
  if (ua.includes("Firefox")) return "Firefox";
  if (ua.includes("SamsungBrowser")) return "Samsung Browser";
  if (ua.includes("Opera") || ua.includes("OPR")) return "Opera";
  if (ua.includes("Trident")) return "Internet Explorer";
  if (ua.includes("Edge")) return "Edge";
  if (ua.includes("Chrome")) return "Chrome";
  if (ua.includes("Safari")) return "Safari";
  return "Unknown";
};

const getOSName = () => {
  const appVersion = navigator.appVersion;
  if (appVersion.indexOf("Win") !== -1) return "Windows";
  if (appVersion.indexOf("Mac") !== -1) return "MacOS";
  if (appVersion.indexOf("X11") !== -1) return "UNIX";
  if (appVersion.indexOf("Linux") !== -1) return "Linux";
  if (navigator.userAgent.indexOf("Android") !== -1) return "Android";
  if (navigator.userAgent.indexOf("iPhone") !== -1) return "iOS";
  return "Unknown";
};

const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return "tablet";
  }
  if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return "mobile";
  }
  return "desktop";
};

const getCurrentPosition = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation not supported"));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: false,
      timeout: 3000, // 3 seconds timeout for fingerprinting
      maximumAge: 300000, // 5 minutes cache
    });
  });
};
